/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("VMSession", ["EventListener", "VMSessionBrowserMultiPageMode"], function (context, cls) {
  /**
   * A VM driven Session
   * @class VMSession
   * @memberOf classes
   * @extends classes.EventListener
   * @publicdoc Base
   */
  cls.VMSession = context.oo.Class(cls.EventListener, function ($super) {
    return /** @lends classes.VMSession.prototype */ {
      __name: "VMSession",
      /**
       * @type {?string}
       */
      _identifier: null,
      /**
       * @type {?string}
       */
      _sessionId: null,
      /**
       * All the applications belonging to the session
       * @type {classes.VMApplication[]}
       */
      _applications: null,

      /**
       * app id bootstrapped by the GAS
       * if not 0, it is loaded as a new tab/window in browser
       * @type {number}
       */
      _htmlHostPageAppId: 0,

      _baseInfos: null,
      _closeHandler: null,
      _restartHandler: null,
      /**
       * @type {classes.SessionWidget}
       */
      _widget: null,
      _applicationIdentifier: 0,
      _applicationQueue: null,

      _waitingNewTasks: 0,
      _showEnding: false,
      _flushingApplications: false,
      _flushingApplicationsListener: false,
      _unloadListener: false,
      _flushableApplications: null,
      /** @type {?string} */
      _logPromptUrl: null,
      /** @type Window */
      _logPromptWindow: null,
      /** @type classes.SessionLogPromptWidget */
      _logPromptWidget: null,
      /** @type string[] */
      _serverFeatures: null,
      /** @type string[] */
      _aiProviders: null,
      /** @type {?string} */
      _endUrl: null,
      /**
       * Mode of the session.
       * - null, GBC run in standard mode
       * - VMSessionBrowserMultiPageMode, GBC run in browser multi page mode
       */
      _sessionMode: null,
      _childWindows: null,

      /** @type {classes.VMSessionNavigationManager} */
      _navigationManager: null,

      /**
       * @inheritDoc
       * @constructs
       * @param {?string} identifier session identifier
       */
      constructor: function (identifier) {
        $super.constructor.call(this);
        this._navigationManager = new cls.VMSessionNavigationManager(this);
        this._htmlHostPageAppId = context.bootstrapInfo.subAppInfo;
        this._widget = cls.WidgetFactory.createWidget("Session", {
          appHash: gbc.systemAppId,
          session: this
        });
        this._widget.when(context.constants.widgetEvents.titleChanged, (title) => context.HostService.setDocumentTitle());
        context.HostService.getApplicationHostWidget().addChildWidget(this._widget);
        this._identifier = identifier;
        this._applications = [];
        this._applicationQueue = [];
        this._childWindows = [];
        this._closeHandler = this._widget.getEndWidget().when(context.constants.widgetEvents.close, () => this.destroy());
        this._restartHandler = this._widget.getEndWidget().when(context.constants.widgetEvents.restart, () => this._onRestart());
        this._missingAPIs = [];

        this._flushableApplications = [];
        // if context.bootstrapInfo.subAppInfo > 0, we are in a child window
        if (this._htmlHostPageAppId) {
          this.activateBrowserMultiPageMode();
        }
        this._flushingApplicationsListener = context.InitService.when(gbc.constants.widgetEvents.onBeforeUnload, () =>
          this._flushWaitingApplications()
        );
        this._unloadListener = context.InitService.when(gbc.constants.widgetEvents.onUnload, () => this._destroyChildrenWindows());
        this._serverFeatures = [];
        this._aiProviders = [];

        // hide or display ChromeBar
        if (!context.ThemeService.getValue("gbc-ChromeBar-show")) {
          context.MainContainerService.hideChromeBar();
        }
      },
      _onRestart: function () {
        if (context.bootstrapInfo.reloadOnRestart === "true") {
          window.location.reload(true);
          return;
        }
        const info = this._baseInfos;
        this.destroy(true);
        context.SessionService.startApplication(info.appId, info.urlParameters);
      },
      getWidget: function () {
        return this._widget;
      },
      getIdentifier: function () {
        return this._identifier;
      },

      /**
       * Return an incremented number everytime the method is called
       * @returns {number}
       */
      getApplicationIdentifier: function () {
        return this._applicationIdentifier++;
      },

      getMissingAPIs: function () {
        return this._missingAPIs;
      },

      destroy: function (restarting) {
        context.SessionService.remove(this, true === restarting);
        if (this._sessionMode) {
          this._sessionMode.destroy();
          this._sessionMode = null;
        }
        this._closeHandler();
        this._restartHandler();

        if (this._navigationManager) {
          this._navigationManager.destroy();
          this._navigationManager = null;
        }
        this._widget.destroy();
        this._widget = null;
        this._applications.length = 0;
        this._applicationQueue.length = 0;
        this._flushingApplicationsListener();
        this._destroyChildrenWindows();
        this._destroyChromeAndSideBars();
        $super.destroy.call(this);
      },
      /**
       * Destroy and reset all chromebars and sidebars related lists and setup
       */
      _destroyChromeAndSideBars: function () {
        // NOTE: why we destroy static service here ? is it really necessary ?
        context.ChromeActionService.destroy();
        context.WindowListService.destroy();
        context.ApplicationListService.destroy();
        context.MainContainerService.destroy();
      },
      getConnector: function () {
        return this._baseInfos.connector;
      },
      /**
       * Get the session ID
       * @return {string} the session ID
       * @publicdoc
       */
      getSessionId: function () {
        return this._sessionId;
      },

      setSessionId: function (id) {
        if (!this._sessionId) {
          this._sessionId = id;
          context.SessionService.updateSessionId(this, id);
        } else if (id !== this._sessionId) {
          this.error("Session Id Changed");
        }
      },
      getAppId: function () {
        return this._baseInfos.appId;
      },
      error: function (msg) {},

      /**
       * @returns {classes.VMSessionNavigationManager}
       */
      getNavigationManager: function () {
        return this._navigationManager;
      },

      /**
       * Register a new application
       * @param {classes.VMApplication} application
       */
      add: function (application) {
        let queueSize = this._applicationQueue.length;
        this._applications.push(application);
        if (queueSize === 0 || this._applicationQueue[queueSize - 1] !== application) {
          this._applicationQueue.push(application);
        }
        application.__idleChangedSessionHook = application.dvm.onIdleChanged(() => this._onIdleChanged(application));

        this.getNavigationManager().addApplication(application);
      },
      /**
       *
       * @param {classes.VMApplication} application
       */
      remove: function (application) {
        application.__idleChangedSessionHook();
        cls.ArrayHelper.remove(this._applications, application);
        cls.ArrayHelper.remove(this._applicationQueue, application);
        this._applicationEnding = application.info().ending;
        let pos = 0;

        while (pos < this._applicationQueue.length) {
          if (this._applicationQueue[pos] === this._applicationQueue[pos + 1]) {
            this._applicationQueue.splice(pos, 1);
          } else {
            pos++;
          }
        }
        this.getNavigationManager().removeApplication(application);

        const currentApp = this.getNewApplication(application);

        if (currentApp) {
          this.setCurrentApplication(currentApp);
          const currentWindow = currentApp.getVMWindow();
          if (currentWindow) {
            const appWidget = currentApp.getUI().getWidget(),
              winWidget = currentWindow.getController().getWidget();

            this.getNavigationManager().setLastActiveWindow(currentApp, currentWindow.getId());
            winWidget.domAttributesMutator(() => appWidget.removeClass("inactiveWindow"));

            this._widget.setCurrentWidget(appWidget);
            context.HostService.setDisplayedWindowNode(currentWindow);
          }
        }

        this._showEnding = true;
        this._updateDisplayEnd();
      },

      _updateDisplayEnd: function () {
        if (this._showEnding && !this._applications.length) {
          if (!this._destroyed) {
            this.getWidget().showWaitingEnd();
          }
          context.HostService.unsetDisplayedWindowNode();

          let delay = 0;

          if (context.BrowserWindowsService.isRootWindow() && this.isInBrowserMultiPageMode()) {
            delay = 1000;
          }

          this._registerTimeout(() => {
            if (
              !(
                (context.BrowserWindowsService.isRootWindow() &&
                  context.BrowserWindowsService.countChildWindows(
                    (w) => !/monitor=true/.test(window._multiWindowData.directChildren[0].location.search)
                  )) ||
                (this.isInBrowserMultiPageMode() && this._waitingNewTasks > 0)
              )
            ) {
              if (this._waitingNewTasks === 0 && !this._destroyed) {
                // reset sidebars and chromebar
                this._destroyChromeAndSideBars();
              }
              this.displayEnd();
            }
          }, delay);
        }
      },
      _autoclose: function () {
        this._registerTimeout(() => {
          const can = !this._flushableApplications || !this._flushableApplications.length;
          if (can) {
            cls.WindowHelper.closeWindow();
          } else {
            this._autoclose();
          }
        }, 200);
      },

      /**
       * Terminate the session
       * (This is always called by window.onunload with forceServer=true)
       * @param {boolean} [forceServer] true to send a session close (/ua/close) to the server (if supported)
       * @publicdoc
       */
      closeSession: function (forceServer) {
        // if forceServer and is not child of another tab (stantdalone or browserMultiPage activated),
        // send session /ua/close if it has feature
        if (forceServer && this.isMasterBrowserPage() && this.hasServerFeature("close-session")) {
          if (this._applications && this._applications[0]) {
            this._applications[0].protocolInterface.closeSession();
          }
          // if forceServer and is child of another tab (browserMultiPage activated)
          // try to send app /ua/close (will free parent app processing if RUN was called)
        } else if (forceServer && !this.isMasterBrowserPage() && this._applications[0]) {
          this._applications[0].close();
        } else if (this._applications) {
          // make a copy of the list because app.stop() updates the applications list
          let appsCopy = [...this._applications];
          for (const app of appsCopy) {
            app.stop();
          }
          appsCopy = null;
        }
      },

      setEndUrl: function (url) {
        this._endUrl = url;
      },

      displayEnd: function () {
        if (!this.isMasterBrowserPage()) {
          this._autoclose();
          return;
        }
        this.emit(context.constants.baseEvents.displayEnd, this._baseInfos.session);
        if (this.getWidget()) {
          this.getWidget().getEndWidget().setHeader(gbc.I18NService.t("gwc.app.ending.title"));
          if (this._baseInfos.session) {
            this.getWidget().getEndWidget().showSessionActions();
            this.getWidget()
              .getEndWidget()
              .setSessionLinks(this._baseInfos.customUA || this._baseInfos.connector || "", this._baseInfos.session);
            this.getWidget().getEndWidget().setSessionID(this._baseInfos.session);
          }
          if (this._baseInfos.mode === "ua") {
            this.getWidget().getEndWidget().showUAActions();
          }
          if (!this._applicationEnding.normal) {
            switch (this._applicationEnding.flag) {
              case "notFound":
                this.getWidget().getEndWidget().setHeader(gbc.I18NService.t("gwc.app.notFound.title"));
                this.getWidget()
                  .getEndWidget()
                  .setMessage(
                    gbc.I18NService.t("gwc.app.notFound.message", {
                      appId: '<strong>"' + this._baseInfos.appId + '"</strong>'
                    })
                  );
                break;
              case "notok":
                this.getWidget()
                  .getEndWidget()
                  .setMessage(
                    "<p data-details='notok'>" + gbc.I18NService.t("gwc.app.error.message") + ".</p><p>" + this._applicationEnding.message + "</p>"
                  );
                break;
              case "forbidden":
                this.getWidget()
                  .getEndWidget()
                  .setMessage(
                    "<p data-details='forbidden'>" +
                      gbc.I18NService.t("gwc.app.forbidden.message") +
                      ".</p><p>" +
                      this._applicationEnding.message +
                      "</p>"
                  );
                break;
              case "autoLogout":
                this.getWidget()
                  .getEndWidget()
                  .setMessage("<p data-details='autoLogout'>" + gbc.I18NService.t("gwc.app.autologout.message") + ".</p>");
                break;
              case "uaProxy":
                this.getWidget()
                  .getEndWidget()
                  .setMessage(
                    "<p data-details='uaProxy'>" +
                      gbc.I18NService.t("gwc.app.uaProxy.message") +
                      ".</p><p>" +
                      this._applicationEnding.message +
                      "</p>"
                  );
                break;
            }
          }

          // if wrapper has an endUrl always use it
          const endUrl = window.gbcWrapper.getSessionEndUrl() || this._endUrl;
          if (endUrl) {
            this.getWidget().showRedirectEnd();
            window.location.href = endUrl;
          } else if (this._applicationEnding.flag !== "hidden") {
            this.getWidget().showEnd();
          }
        }
        this.displayLogPrompt();
        context.HostService.unsetDisplayedWindowNode();
      },

      displayLogPrompt: function (promptUrl) {
        const latePromptHandling = !this._logPromptUrl && Boolean(promptUrl);
        if (!latePromptHandling) {
          this._trackPromptEnding();
        }
        if (this._logPromptUrl !== promptUrl) {
          this._logPromptUrl = promptUrl;
          if (!this._logPromptUrl) {
            document.body.classList.remove("logPrompting");
            if (this._logPromptWindow) {
              this._logPromptWindow.close();
              this._logPromptWindow = null;
            }
            if (this._logPromptWidgetHandle) {
              this._logPromptWidgetHandle();
            }
            if (this._logPromptWidget) {
              this._logPromptWidget.destroy();
              this._logPromptWidget = null;
            }
          } else {
            if (latePromptHandling) {
              this._trackPromptEnding();
            }
            if (!this._logPromptWidget) {
              this._logPromptWidget = cls.WidgetFactory.createWidget("SessionLogPrompt", {
                appHash: gbc.systemAppId
              });
              this._logPromptWidgetHandle = this._logPromptWidget.when(
                context.constants.widgetEvents.click,
                function () {
                  if (this.isMasterBrowserPage()) {
                    this._displayLogPopup();
                  } else {
                    const opener = window.opener,
                      openerSession = opener && opener.gbc && opener.gbc.SessionService.getCurrent();
                    if (openerSession) {
                      openerSession._displayLogPopup();
                    }
                  }
                }.bind(this)
              );
              document.body.appendChild(this._logPromptWidget.getElement());
            }
            if (this.isMasterBrowserPage()) {
              if (!this._logPromptWindow) {
                document.body.classList.add("logPrompting");
              } else {
                this._logPromptWindow.location.href = this._logPromptUrl;
              }
            }
          }
        }
      },
      _displayLogPopup: function () {
        if (!this._logPromptWindow || this._logPromptWindow.closed) {
          this._logPromptWindow = window.open(
            this._logPromptUrl,
            "",
            "resizable,scrollbars,status,width=" +
              window.innerWidth +
              ",height=" +
              window.innerHeight +
              ",top=" +
              window.screenTop +
              ",left=" +
              window.screenLeft
          );
        } else if (this._logPromptWindow) {
          this._logPromptWindow.focus();
        }
      },

      _trackPromptEnding: function () {
        if (this._logPromptUrl) {
          let hasOnlyIdle = true;
          const protocolInterface = this.getApplications()[0] && this.getApplications()[0].protocolInterface,
            protocolAlive = protocolInterface && protocolInterface.isAlive();
          this.getApplications().forEach(function (app) {
            hasOnlyIdle = hasOnlyIdle && app.isIdle();
          });
          if (hasOnlyIdle && protocolAlive) {
            protocolInterface.trackPrompt();
          }
        }
      },

      /**
       *
       * @param {string[]} features
       */
      addServerFeatures: function (features) {
        for (const item of features) {
          const feature = item.trim();
          if (this._serverFeatures.indexOf(feature) < 0) {
            this._serverFeatures.push(feature);
          }
        }
      },
      /**
       *
       * @param {string} feature
       * @return {boolean}
       */
      hasServerFeature: function (feature) {
        return Boolean(this._serverFeatures) && this._serverFeatures.indexOf(feature) >= 0;
      },
      /**
       * Add AI provider ids advertised by the GAS (deduplicated)
       * @param {string[]} providers
       */
      addAiProviders: function (providers) {
        for (const item of providers) {
          const provider = item.trim();
          if (provider && !this._aiProviders.includes(provider)) {
            this._aiProviders.push(provider);
          }
        }
      },
      /**
       * @return {string[]} AI provider ids registered on the connected GAS
       */
      getAiProviders: function () {
        return this._aiProviders ? this._aiProviders.slice() : [];
      },
      /**
       *
       * @returns {boolean}
       */
      isEmpty: function () {
        return !this._applications.length;
      },

      /**
       * Start this session
       * @param {string} appName
       * @param {Object} params
       * @returns {VMApplication} The newly created application
       */
      start: function (appName, params) {
        const info = new cls.VMApplicationInfo({
          appId: appName,
          urlParameters:
            params ||
            (context.bootstrapInfo.queryString
              ? new cls.QueryString(context.bootstrapInfo.queryString).copyContentsObject()
              : context.UrlService.currentUrl().getQueryStringObject())
        });
        info.connector = info.urlParameters.connector || context.bootstrapInfo.connectorUri || "";
        info.customUA = info.urlParameters.customUA || null;
        info.mode = info.urlParameters.mode || "ua";
        info.inNewWindow = !this.isMasterBrowserPage();
        if (info.inNewWindow) {
          info.session = this._sessionId = context.bootstrapInfo.sessionId;
        }
        this._baseInfos = info;
        const application = new cls.VMApplication(info, this);
        const appWidget = application.getUI().getWidget();
        this.add(application);
        this._widget.setCurrentWidget(appWidget);
        application.start();
        this._registerNewTask(application.protocolInterface);
        return application;
      },

      /**
       * Start an application as a child of this session
       * @param {number} taskId
       * @param {number} taskProcIdParent
       * @param {boolean} taskWaiting
       * @param {Function} callback
       */
      startSubTask: function (taskId, taskProcIdParent = null, taskWaiting = null, callback = () => {}) {
        let info = {
          ...(this._baseInfos || {})
        };
        info.connectionInfo = {};
        info.urlParameters = {
          ...(this._baseInfos.urlParameters || {})
        };
        info.inNewWindow = this.isInBrowserMultiPageMode() || (!this.isMasterBrowserPage() && this._htmlHostPageAppId !== taskId);

        if (info.inNewWindow) {
          info.urlParameters = context.bootstrapInfo.queryString
            ? new cls.QueryString(context.bootstrapInfo.queryString).copyContentsObject()
            : context.UrlService.currentUrl().getQueryStringObject();
          info.connector = info.urlParameters.connector || context.bootstrapInfo.connectorUri || "";
          info.customUA = info.urlParameters.customUA || null;
          info.mode = info.urlParameters.mode || "ua";
        }
        if (!this.isMasterBrowserPage()) {
          this._sessionId = context.bootstrapInfo.sessionId;
          info.session = this._sessionId;
        }

        info.task = true;
        info.page = 2;
        info.app = taskId;
        info.taskProcIdParent = taskProcIdParent;
        /* True if this task is awaited by it's invoker */
        info.taskWaiting = taskWaiting;

        let hostingWindow =
          taskWaiting &&
          window._multiWindowData.findWindowBy((w) => {
            const session = w && w.gbc && w.gbc.SessionService.getCurrent();
            if (session) {
              return session.getApplications().find((app) => app.info() && app.info().connectionInfo.procId === taskProcIdParent);
            }
            return null;
          });
        info.inNewWindow = info.inNewWindow && !hostingWindow;
        if (!info.inNewWindow) {
          const session = hostingWindow ? hostingWindow.gbc.SessionService.getCurrent() : this;
          session._startSubTaskLocally(info, callback);
        } else {
          const application = new cls.VMApplication(new cls.VMApplicationInfo(info), this);
          application.protocolInterface.waitForApplicationInNewWindow(
            () => {
              this.waitedForNewTask();
              callback(application);
            },
            () => {}
          );
        }
      },

      _startSubTaskLocally: function (info, callback) {
        let application = new cls.VMApplication(new cls.VMApplicationInfo(info), this);
        this.add(application);
        const appWidget = application.getUI().getWidget();
        this._widget.setCurrentWidget(appWidget);
        application.start();
        this.waitedForNewTask();
        callback(application);
      },

      /**
       *
       * @param {window.gbcWrapper} wrapper
       * @param {Object<string, *>} readyData
       */
      startDirect: function (wrapper, readyData) {
        let meta = cls.AuiProtocolReader.translate(readyData.meta)[0];
        const info = new cls.VMApplicationInfo({
          pingTimeout: 1000,
          connectionInfo: meta.attributes,
          procId: meta.attributes.procId,
          taskWaiting: meta.attributes.procIdWaiting && meta.attributes.procIdWaiting === meta.attributes.procIdParent,
          page: 1,
          auiOrder: 0,
          mode: "direct",
          nativeResourcePrefix: readyData.nativeResourcePrefix.replace(/\/$/, "") + "/__dvm__/"
        });
        if (readyData.headers) {
          const headersKeys = Object.keys(context.constants.network.startHeaders);
          for (const key of headersKeys) {
            const value = context.constants.network.startHeaders[key];
            info[value.prop || key] = readyData.headers[context.constants.network.headers[key]];
          }
        }

        gbc.classes.EncodingHelper.setVMEncoding(meta.attributes.encoding.toLowerCase());

        info.wrapper = wrapper;
        this._baseInfos = info;
        const application = new cls.VMApplication(info, this);
        application.setProcessing();
        application.procId = info.procId;
        const appWidget = application.getUI().getWidget();
        this.add(application);
        this._widget.setCurrentWidget(appWidget);
        this._navigationManager.updateApplicationInformation(application);
        application.start();
      },

      info: function () {
        return this._baseInfos;
      },

      /**
       * Get all running applications
       * @returns {classes.VMApplication[]} an array of applications or null
       * @publicdoc
       */
      getApplications: function () {
        return this._applications;
      },

      /**
       * Get the current application
       * @returns {classes.VMApplication}
       */
      getCurrentApplication: function () {
        if (this._applications.length && this._applications.at(-1)) {
          return this._applications.at(-1);
        }
        return null;
      },

      /**
       *
       * @returns {classes.VMApplication}
       */
      getApplicationByHash: function (hash) {
        return (
          this._applications &&
          this._applications.filter(function (item) {
            return item.applicationHash === hash;
          })[0]
        );
      },
      getApplicationByProcId: function (procId) {
        return (
          this._applications &&
          this._applications.filter(function (item) {
            return item.getProcId() === procId;
          })[0]
        );
      },
      /**
       * Set the application given in parameter as the first one
       * in the application list.
       * Please call NavigationService.displayApplication instead of this method
       * @param {VMApplication} application
       */
      setCurrentApplication: function (application) {
        const index = this._applications.indexOf(application);
        if (index !== -1) {
          this._applications.splice(index, 1);
          this._applications.push(application);
        }
      },

      _onIdleChanged: function (application) {
        this.emit(context.constants.baseEvents.idleChanged, application);
      },
      whenIdleChanged: function (hook) {
        return this.when(context.constants.baseEvents.idleChanged, hook);
      },
      isCurrentIdle: function () {
        const app = this.getCurrentApplication();
        return !app || (app.dvm.processed && app.dvm.idle && app.layout.isIdle() && app.scheduler.hasNoCommandToProcess());
      },

      _addWaitingApplication: function (application) {
        this._flushableApplications.push(application);
      },
      _removeWaitingApplication: function (application) {
        cls.ArrayHelper.remove(this._flushableApplications, application);
      },
      _flushWaitingApplications: function () {
        this._flushingApplications = true;
        while (this._flushableApplications && this._flushableApplications.length) {
          cls.WindowHelper.openWindow(
            cls.UANetwork.newApp(this._flushableApplications.shift(), null, null, {
              noQueryString: true
            }),
            true
          );
        }
      },
      _registerNewTask: function (protocolInterface) {
        this._storedProtocol = protocolInterface;
      },
      newTask: function () {
        if (this._storedProtocol) {
          this._storedProtocol.newTask();
          // Init GBC specific menu items
        }
      },
      _registerChildWindow: function (win) {
        if (!win) {
          return;
        }
        window._emergencyClose = window._emergencyClose || {};
        win._opener = window;
        win.addEventListener(
          "load",
          function () {
            window._emergencyClose[win.gbc.uid] = [];
            win.addEventListener(
              "unload",
              function () {
                cls.ArrayHelper.remove(this._childWindows, win);
                if (window.browserInfo.isFirefox) {
                  const emergencyCloses = window._emergencyClose[win.gbc.uid];
                  for (const closeInfo of emergencyCloses) {
                    cls.UANetwork.querySend("close", null, closeInfo, function () {}, null, null);
                  }
                }
                if (win.location.href !== "about:blank") {
                  // thank you firefox
                  this._updateDisplayEnd();
                }
              }.bind(this)
            );
          }.bind(this)
        );
        this._childWindows.push(win);
      },
      _destroyChildrenWindows: function () {
        this._childWindows = [];
        context.BrowserWindowsService.closeAllChildren();
      },
      waitingForNewTask: function () {
        this._waitingNewTasks++;
      },

      waitedForNewTask: function () {
        if (this._waitingNewTasks > 0) {
          this._waitingNewTasks--;
        }
        this._updateDisplayEnd();
      },

      isInBrowserMultiPageMode: function () {
        return this._sessionMode && this._sessionMode.isInstanceOf(cls.VMSessionBrowserMultiPageMode);
      },

      isMasterBrowserPage: function () {
        return !this._htmlHostPageAppId;
      },

      activateBrowserMultiPageMode: function () {
        if (!this._sessionMode && this.getApplications().length === 1) {
          this._sessionMode = new cls.VMSessionBrowserMultiPageMode(this);
        }
      },

      /**
       * '
       * @param {classes.VMApplication} app
       * @param {classes.ApplicationWidget} appWidget
       */
      addApplicationWidget: function (app, appWidget) {
        this.getWidget().addChildWidget(appWidget);
      },

      manageStartMenu: function (startMenuNode, widget) {
        startMenuNode.getAncestor("UserInterface").getController().getWidget().addStartMenu(widget);
      },

      /**
       * Get the running application corresponding to the remove application
       * @param removedApplication
       * @return {classes.VMApplication}
       */
      getNewApplication: function (removedApplication) {
        let parentProcId = removedApplication.getProcIdParent();

        if (!parentProcId || this._applicationQueue.length === 1) {
          return this._applicationQueue[this._applicationQueue.length - 1];
        }

        const app = this._navigationManager.getApplicationByProcId(parentProcId);

        // if no app has been found return the last created one
        if (!app && this._applicationQueue.length > 0) {
          return cls.ArrayHelper.last(this._applicationQueue);
        } else {
          // return parent app or sibling app if no parent
          return app;
        }
      },

      /**
       * Get the application with identifier = applicationIdentifier
       * @param {number} applicationIdentifier
       * @return {classes.VMApplication|undefined}
       */
      getApplicationByIdentifier: function (applicationIdentifier) {
        return this._applications.find((app) => {
          return app.applicationHash === applicationIdentifier;
        });
      },

      /**
       * Checks for required APIs availability and triggers an alert if necessary.
       */
      checkEnvironmentAndAlert: function () {
        if (!context.ThemeService.getValue("gbc-enable-degraded-mode-alerts")) {
          return;
        }

        this._missingAPIs = [];
        const requiredAPIs = gbc.constants.requiredAPIs;
        const platform = cls.WindowHelper.isMobile() ? "mobile" : "desktop";

        // Check if running in a secure context
        if (!window.isSecureContext) {
          this._missingAPIs.push(gbc.I18NService.t("gwc.degradedModeAlerts.notSecureContext"));
        }

        // Verify that the runtime (FGL) and server (GAS) versions meet the required minimums.
        this._checkVersion(gbc.info().runtimeVersion, requiredAPIs.minimumRequiredVersions.FGLGWS, "FGL");
        this._checkVersion(gbc.info().serverVersion, requiredAPIs.minimumRequiredVersions.GAS, "GAS");

        // Check common APIs required on all platforms
        this._checkAPIs("common", requiredAPIs);

        // Check platform-specific APIs (mobile or desktop)
        if (requiredAPIs[platform]) {
          this._checkAPIs(platform, requiredAPIs);
        }

        if (this._missingAPIs.length > 0) {
          console.log(gbc.I18NService.t("gwc.degradedModeAlerts.degradedMode"), this._missingAPIs);
          this.emit("environmentAlert", this._missingAPIs);
        }
      },

      /**
       * Check if a version is provided and satisfies the minimum required version.
       * @param {string} version - The current version.
       * @param {string} requiredVersion - The minimum required version.
       * @param {string} versionType - The type of version being checked (e.g., "FGL", "GAS").
       * @private
       */
      _checkVersion: function (version, requiredVersion, versionType) {
        if (!version) {
          console.log(gbc.I18NService.t("gwc.degradedModeAlerts.versionUnavailable").replace("{versionType}", versionType));
          return;
        }

        const currentVersion = this._formatVersion(version);
        if (currentVersion >= requiredVersion) {
          return;
        }

        this._missingAPIs.push(
          gbc.I18NService.t(`gwc.degradedModeAlerts.unsupported${versionType}Version`)
            .replace("{current}", currentVersion)
            .replace("{required}", requiredVersion)
        );
      },

      /**
       * Check the availability of required APIs.
       * @param {string} category - API category to check (e.g., "common", "mobile").
       * @param {Object} requiredAPIs - Object containing the required APIs.
       * @private
       */
      _checkAPIs: function (category, requiredAPIs) {
        for (const context in requiredAPIs[category]) {
          if (typeof window[context] === "undefined") {
            continue;
          }

          for (const api of requiredAPIs[category][context]) {
            if (!(api in window[context])) {
              this._missingAPIs.push(gbc.I18NService.t("gwc.degradedModeAlerts.apiNotAvailable").replace("{api}", api));
            }
          }
        }
      },

      /**
       * Extracts the version string in the format "X.XX.XX" from a given input string.
       *
       * @param {string} versionString
       * @returns {string}
       * @private
       */
      _formatVersion: function (versionString) {
        const match = versionString.match(/\b\d+\.\d{2}\.\d{2}\b/);
        return match ? match[0] : "0.0.0";
      }
    };
  });
});
